import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminDashboard.css';
import {
  fetchDashboardSummary,
  fetchAdminUserList,
  DashboardSummary,
  AdminUserDetail,
} from '../../../services/adminApi.ts';

/* ── Helpers ─────────────────────────────────────────────────── */
const todayStr = () => new Date().toISOString().split('T')[0];
const oneYearAgoStr = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().split('T')[0];
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);

// Backend sends naive LocalDateTime strings (no timezone suffix) that represent UTC.
// Append 'Z' so the JS Date is parsed as UTC before converting to IST for display.
const parseUtcDate = (date: string) =>
  new Date(/[zZ]|[+-]\d\d:\d\d$/.test(date) ? date : `${date}Z`);

const formatDate = (date: string | null) =>
  date
    ? parseUtcDate(date).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: 'Asia/Kolkata',
      })
    : '—';

const formatDateTimeIST = (date: string | null) =>
  date
    ? parseUtcDate(date).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata',
      }) + ' IST'
    : '—';

const avatarColors = [
  '#6366f1', '#0ea5e9', '#f59e0b', '#10b981',
  '#ef4444', '#8b5cf6', '#14b8a6', '#f97316',
];
const getAvatarColor = (id: number) => avatarColors[id % avatarColors.length];

type FilterType = 'all' | 'premium' | 'free';
type SortKey = 'name' | 'premium' | 'plan' | 'startDate' | 'endDate' | 'amountPaid' | 'joinedAt';
type SortDir = 'asc' | 'desc';

/* ── Component ───────────────────────────────────────────────── */
const AdminDashboard = () => {
  const navigate = useNavigate();

  // Date range
  const [fromDate, setFromDate] = useState(oneYearAgoStr());
  const [toDate, setToDate]     = useState(todayStr());

  // Summary state
  const [summary, setSummary]         = useState<DashboardSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError]     = useState<string | null>(null);

  // User list state
  const [users, setUsers]           = useState<AdminUserDetail[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError]     = useState<string | null>(null);

  // Table controls
  const [search, setSearch]         = useState('');
  const [userFilter, setUserFilter] = useState<FilterType>('all');
  const [pageSize, setPageSize]     = useState(10);
  const [page, setPage]             = useState(1);
  const [sortKey, setSortKey]       = useState<SortKey | null>(null);
  const [sortDir, setSortDir]       = useState<SortDir>('asc');

  /* ── Fetch both APIs ──────────────────────────────────────── */
  const loadData = useCallback(async (from: string, to: string) => {
    setSummaryLoading(true);
    setSummaryError(null);
    setUsersLoading(true);
    setUsersError(null);

    const [summaryResult, usersResult] = await Promise.allSettled([
      fetchDashboardSummary(from, to),
      fetchAdminUserList(from, to),
    ]);

    if (summaryResult.status === 'fulfilled') {
      setSummary(summaryResult.value);
    } else {
      setSummaryError('Failed to load summary.');
    }
    setSummaryLoading(false);

    if (usersResult.status === 'fulfilled') {
      setUsers(usersResult.value);
    } else {
      setUsersError('Failed to load users.');
    }
    setUsersLoading(false);
  }, []);

  // Load on mount and whenever date range changes (via Apply)
  useEffect(() => {
    loadData(fromDate, toDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => {
    if (!fromDate || !toDate) return;
    setPage(1);
    loadData(fromDate, toDate);
  };

  const handleReset = () => {
    const f = oneYearAgoStr();
    const t = todayStr();
    setFromDate(f);
    setToDate(t);
    setPage(1);
    loadData(f, t);
  };

  /* ── Client-side filter + search ─────────────────────────── */
  const filtered = useMemo(() => {
    return users.filter(u => {
      const matchFilter =
        userFilter === 'all' ||
        (userFilter === 'premium' && u.premium) ||
        (userFilter === 'free' && !u.premium);
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q);
      return matchFilter && matchSearch;
    });
  }, [users, userFilter, search]);

  /* ── Client-side sort ────────────────────────────────────── */
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const toComparable = (u: AdminUserDetail): string | number => {
      switch (sortKey) {
        case 'name':      return u.name.toLowerCase();
        case 'premium':   return u.premium ? 1 : 0;
        case 'plan':      return (u.plan ?? '').toLowerCase();
        case 'startDate': return u.startDate ? new Date(u.startDate).getTime() : -Infinity;
        case 'endDate':   return u.endDate ? new Date(u.endDate).getTime() : -Infinity;
        case 'joinedAt':  return u.joinedAt ? new Date(u.joinedAt).getTime() : -Infinity;
        case 'amountPaid': return u.amountPaid ?? -Infinity;
        default: return 0;
      }
    };
    const arr = [...filtered];
    arr.sort((a, b) => {
      const va = toComparable(a);
      const vb = toComparable(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const paginated  = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  const handleFilterChange = (f: FilterType) => { setUserFilter(f); setPage(1); };
  const handleSearch = (v: string) => { setSearch(v); setPage(1); };
  const handlePageSize = (v: number) => { setPageSize(v); setPage(1); };
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };
  const sortArrow = (key: SortKey) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  /* ── Render ───────────────────────────────────────────────── */
  return (
    <div className="main-content">
      <div className="adm-dash">

        {/* ── Page header ──────────────────────────────────── */}
        <div className="adm-dash__header">
          <div className="adm-dash__title-row">
            <button className="adm-back-btn" onClick={() => navigate(-1)}>←</button>
            <div>
              <h1 className="adm-dash__title">User Dashboard</h1>
              <p className="adm-dash__subtitle">
                {fromDate} → {toDate}
              </p>
            </div>
          </div>

          <div className="adm-dash__date-row">
            <div className="adm-date-group">
              <label className="adm-date-label">From</label>
              <input
                type="date"
                className="adm-date-input"
                value={fromDate}
                max={toDate}
                onChange={e => setFromDate(e.target.value)}
              />
            </div>
            <div className="adm-date-group">
              <label className="adm-date-label">To</label>
              <input
                type="date"
                className="adm-date-input"
                value={toDate}
                min={fromDate}
                max={todayStr()}
                onChange={e => setToDate(e.target.value)}
              />
            </div>
            <button
              className="adm-apply-btn"
              onClick={handleApply}
              disabled={summaryLoading || usersLoading}
            >
              {summaryLoading || usersLoading ? 'Loading…' : 'Apply'}
            </button>
            <button className="adm-reset-btn" onClick={handleReset}>Reset</button>
          </div>
        </div>

        {/* ── Stat cards ───────────────────────────────────── */}
        {summaryError ? (
          <div className="adm-error-banner">{summaryError}</div>
        ) : (
          <div className={`adm-stats-grid ${summaryLoading ? 'adm-loading' : ''}`}>
            <div className="adm-stat-card adm-stat-card--blue">
              <div className="adm-stat-icon">👥</div>
              <div className="adm-stat-body">
                <span className="adm-stat-value">
                  {summaryLoading ? '—' : (summary?.totalUsers ?? 0).toLocaleString()}
                </span>
                <span className="adm-stat-label">Total Users</span>
              </div>
            </div>
            <div className="adm-stat-card adm-stat-card--indigo">
              <div className="adm-stat-icon">💎</div>
              <div className="adm-stat-body">
                <span className="adm-stat-value">
                  {summaryLoading ? '—' : (summary?.premiumUsers ?? 0).toLocaleString()}
                </span>
                <span className="adm-stat-label">Premium Users</span>
              </div>
            </div>
            <div className="adm-stat-card adm-stat-card--slate">
              <div className="adm-stat-icon">🆓</div>
              <div className="adm-stat-body">
                <span className="adm-stat-value">
                  {summaryLoading ? '—' : (summary?.freeUsers ?? 0).toLocaleString()}
                </span>
                <span className="adm-stat-label">Free Users</span>
              </div>
            </div>
            <div className="adm-stat-card adm-stat-card--green">
              <div className="adm-stat-icon">💰</div>
              <div className="adm-stat-body">
                <span className="adm-stat-value">
                  {summaryLoading ? '—' : formatCurrency(summary?.totalRevenue ?? 0)}
                </span>
                <span className="adm-stat-label">Total Revenue</span>
              </div>
            </div>
          </div>
        )}

        {/* ── User list ────────────────────────────────────── */}
        <div className="adm-user-section">

          {/* toolbar */}
          <div className="adm-user-toolbar">
            <div className="adm-filter-tabs">
              {(['all', 'premium', 'free'] as FilterType[]).map(f => (
                <button
                  key={f}
                  className={`adm-filter-tab ${userFilter === f ? 'adm-filter-tab--active' : ''}`}
                  onClick={() => handleFilterChange(f)}
                >
                  {f === 'all' ? 'All Users' : f === 'premium' ? '💎 Premium' : '🆓 Free'}
                </button>
              ))}
            </div>

            <div className="adm-user-toolbar-right">
              <div className="adm-search-wrap">
                <span className="adm-search-icon">🔍</span>
                <input
                  className="adm-search-input"
                  placeholder="Search name or email…"
                  value={search}
                  onChange={e => handleSearch(e.target.value)}
                />
                {search && (
                  <button className="adm-search-clear" onClick={() => handleSearch('')}>✕</button>
                )}
              </div>

              <div className="adm-pagesize-wrap">
                <span className="adm-pagesize-label">Show</span>
                {[10, 20, 50, 100].map(s => (
                  <button
                    key={s}
                    className={`adm-pagesize-btn ${pageSize === s ? 'adm-pagesize-btn--active' : ''}`}
                    onClick={() => handlePageSize(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* table */}
          <div className="adm-user-table-wrap">
            {usersError ? (
              <div className="adm-error-banner" style={{ margin: '24px 16px' }}>{usersError}</div>
            ) : (
              <table className="adm-user-table">
                <thead>
                  <tr>
                    <th className="adm-th-sortable" onClick={() => handleSort('name')}>User{sortArrow('name')}</th>
                    <th className="adm-th-sortable" onClick={() => handleSort('premium')}>Status{sortArrow('premium')}</th>
                    <th className="adm-th-sortable" onClick={() => handleSort('plan')}>Plan{sortArrow('plan')}</th>
                    <th className="adm-th-sortable" onClick={() => handleSort('startDate')}>Purchased On{sortArrow('startDate')}</th>
                    <th className="adm-th-sortable" onClick={() => handleSort('startDate')}>Start Date{sortArrow('startDate')}</th>
                    <th className="adm-th-sortable" onClick={() => handleSort('endDate')}>End Date{sortArrow('endDate')}</th>
                    <th className="adm-th-sortable" onClick={() => handleSort('amountPaid')}>Amount Paid{sortArrow('amountPaid')}</th>
                    <th className="adm-th-sortable" onClick={() => handleSort('joinedAt')}>Joined At{sortArrow('joinedAt')}</th>
                  </tr>
                </thead>
                <tbody>
                  {usersLoading ? (
                    <tr>
                      <td colSpan={8} className="adm-empty-row">Loading users…</td>
                    </tr>
                  ) : paginated.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="adm-empty-row">No users found</td>
                    </tr>
                  ) : paginated.map(u => (
                    <tr key={u.id}>
                      <td>
                        <div className="adm-user-cell">
                          <span
                            className="adm-avatar"
                            style={{ background: getAvatarColor(u.id) }}
                          >
                            {u.name.charAt(0).toUpperCase()}
                          </span>
                          <div className="adm-user-info">
                            <span className="adm-user-name">{u.name}</span>
                            <span className="adm-user-email">{u.email}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        {u.premium
                          ? <span className="adm-badge adm-badge--premium">💎 Premium</span>
                          : <span className="adm-badge adm-badge--free">Free</span>}
                      </td>
                      <td className="adm-cell-muted">{u.plan ?? '—'}</td>
                      <td className="adm-cell-muted">{u.premium ? formatDateTimeIST(u.startDate) : '—'}</td>
                      <td className="adm-cell-muted">{formatDate(u.startDate)}</td>
                      <td className="adm-cell-muted">{formatDate(u.endDate)}</td>
                      <td className="adm-cell-amount">
                        {u.amountPaid != null ? formatCurrency(u.amountPaid) : '—'}
                      </td>
                      <td className="adm-cell-muted">{formatDate(u.joinedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* pagination */}
          {!usersLoading && !usersError && (
            <div className="adm-pagination">
              <span className="adm-pagination-info">
                Showing{' '}
                {filtered.length === 0 ? 0 : (safePage - 1) * pageSize + 1}–
                {Math.min(safePage * pageSize, filtered.length)} of {filtered.length} users
              </span>
              <div className="adm-pagination-btns">
                <button className="adm-page-btn" disabled={safePage === 1} onClick={() => setPage(1)}>«</button>
                <button className="adm-page-btn" disabled={safePage === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                  .reduce<(number | '...')[]>((acc, p, i, arr) => {
                    if (i > 0 && (p as number) - (arr[i - 1] as number) > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === '...'
                      ? <span key={`e${i}`} className="adm-page-ellipsis">…</span>
                      : <button
                          key={p}
                          className={`adm-page-btn ${safePage === p ? 'adm-page-btn--active' : ''}`}
                          onClick={() => setPage(p as number)}
                        >
                          {p}
                        </button>
                  )}
                <button className="adm-page-btn" disabled={safePage === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
                <button className="adm-page-btn" disabled={safePage === totalPages} onClick={() => setPage(totalPages)}>»</button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
